import axios from 'axios'
import {
  execute,
  type GraphQLExecutor,
  setGraphQLExecutor,
} from '@/graphql/gen/auth'
import type {Fetcher, ObjectFetcher} from 'graphql-ts-client-api'
import {useAuthStore} from '@/stores/auth'
import {
  type FetchRequestAttrs,
  type FetchResponse,
  type FetchResponseError,
  FetchResponseType,
} from '@/types/Fetch'
import {buildQuery, buildBatchQuery, type BatchQueryItem, type Field, type QueryType} from '@/helpers/genGraphql'
import {adaptRequestParams} from '@/helpers/queryParamsAdapter'
import {normalizeFiltersVariables} from '@/helpers/filtersArg'
import {localeStorage} from '@/helpers/storage'
import {ElNotification} from 'element-plus'
import {Locale} from '@/composables/locale'

export {FetchResponseType}

export class GraphQLSrv {
  private apiUrl = import.meta.env.VITE_API_URL_GRAPHQL
  constructor(url?: string) {
    if (url) {
      this.apiUrl = url
    }
    this.initExecutor()
  }

  private getStatusFromError(err: any): number | undefined {
    return err?.response?.status ?? err?.status
  }

  private async handleUnauthorized(err: any): Promise<boolean> {
    const status = this.getStatusFromError(err)
    if (status === 401) {
      await useAuthStore().logout()
      return true
    }
    return false
  }

  private initExecutor() {
    setGraphQLExecutor(async (request, variables) => {
      const authStore = useAuthStore()
      const headers: { [key: string]: string } = {}
      if (authStore.isAuth) {
        headers['Authorization'] = `Bearer ${authStore.accessToken}`
      }
      const response = await axios.post(
        this.apiUrl,
        {
          query: request,
          variables: normalizeFiltersVariables(variables, request.slice(0, 80)),
        },
        {
          headers: {
            'Content-Type': 'application/json',
            ...headers,
          },
        },
      )
      return await response.data
    })
  }

  protected collectAttributes(
    attrs: string[],
    query: ObjectFetcher<any, object, object>,
    fields: ObjectFetcher<any, object, object>,
  ) {
    return attrs.reduce<ObjectFetcher<any, object, object> | any>(
      (acc, attr) => {
        if (fields.fieldMap.has(attr) && attr !== 'attributes') {
          return acc[attr]
        }

        return acc
      },
      query,
    )
  }

  protected getResponse<TData extends object>(
    res: TData | FetchResponseError,
    message?: string,
  ): FetchResponse<TData> {
    if (res && typeof res === 'object' && 'errors' in res && Array.isArray(res.errors)) {
      const error = res.errors[0]
      let errorMessage = error?.message ?? ''
      let validationErrors: Record<string, string[]> | undefined

      if (error?.extensions?.validation) {
        validationErrors = error.extensions.validation
        errorMessage = error?.message ?? ''
      }

      return {
        type: FetchResponseType.ERROR,
        message: errorMessage,
        data: null,
        validationErrors,
      }
    }

    return {
      type: FetchResponseType.SUCCESS,
      message: message ?? '',
      data: res as TData,
    }
  }

  private showValidationErrors(validationErrors: Record<string, string[]>) {
    Object.entries(validationErrors).forEach(([, errors]) => {
      errors.forEach((error, index) => {
        setTimeout(() => {
          ElNotification({
            type: 'error',
            duration: 5000,
            message: error,
            position: 'bottom-right',
          })
        }, index * 100)
      })
    })
  }

  private showErrorNotification(message: string) {
    ElNotification({
      type: 'error',
      duration: 3000,
      message,
      position: 'bottom-right',
    })
  }

  protected async fetch<TData extends object, TVariables extends object>(
    fetcher: Fetcher<'Query' | 'Mutation', TData, TVariables>,
    options?: {
      readonly operationName?: string
      readonly variables?: TVariables
      readonly executor?: GraphQLExecutor
    },
  ): Promise<TData & FetchResponseError> {
    try {
      return execute(fetcher, options)
    } catch (error) {
      const err = error as FetchResponseError
      throw {
        errors: err.errors ?? [],
      }
    }
  }

  protected async axios(query: string) {
    const authStore = useAuthStore()

    const headers: Record<string, string> = {
      'Accept-Language': localeStorage.get() || Locale.Ru,
    }

    if (authStore.isAuth) {
      headers['Authorization'] = `Bearer ${authStore.accessToken}`
    }

    const response = await axios.post(
      this.apiUrl,
      {
        query,
      },
      {
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
      },
    )
    return response.data
  }

  public async query(
    requestName: string,
    responseAttrs: Field[],
    includePagination = true,
    requestAttrs?: FetchRequestAttrs,
  ) {
    const res = await this.request('query', requestName, responseAttrs, includePagination, requestAttrs)

    return {
      ...res,
      list: res.data?.data,
    }
  }

  /**
   * Handle file uploads with proper multipart/form-data format for GraphQL
   * @param formData FormData containing operations, map and file
   * @param onProgress Optional progress callback
   * @returns FetchResponse with data including uuid
   */
  public async uploadFile(formData: FormData, onProgress?: (event: any) => void): Promise<FetchResponse<any>> {
    try {
      const authStore = useAuthStore()

      const headers: Record<string, string> = {
        'Accept-Language': localeStorage.get() || Locale.Ru,
      }

      if (authStore.isAuth) {
        headers['Authorization'] = `Bearer ${authStore.accessToken}`
      }

      const response = await axios.post(
        this.apiUrl,
        formData,
        {
          headers,
          onUploadProgress: (evt) => {
            if (onProgress) {
              const percent = Math.round((evt.loaded * 100) / (evt.total || 1))
              onProgress({percent})
            }
          },
        },
      )

      if (response.data.errors) {
        throw response.data
      }

      return this.getResponse(response.data.data?.uploadFile)
    } catch (err: any) {
      console.error('Error uploading file: ', err)
      const res = this.getResponse<any>(err as FetchResponseError)

      if (res.validationErrors) {
        this.showValidationErrors(res.validationErrors)
      } else {
        this.showErrorNotification(res.message)
      }

      return res
    }
  }

  public mutation(
    requestName: string,
    responseAttrs: Field[],
    requestAttrs?: FetchRequestAttrs,
  ) {
    return this.request('mutation', requestName, responseAttrs, false, requestAttrs)
  }

  public async request(
    requestType: QueryType,
    requestName: string,
    responseAttrs: Field[],
    includePagination = true,
    requestAttrs?: FetchRequestAttrs,
  ) {
    try {
      const adaptedAttrs = adaptRequestParams(requestName, requestAttrs)
      const query = buildQuery(
        requestType,
        requestName,
        responseAttrs,
        includePagination,
        adaptedAttrs,
      )
      // console.warn(`debug query from ${requestName}: `, query)
      const response = await this.axios(query)

      if (response.errors) {
        throw response
      }

      return this.getResponse(response.data?.[requestName])
    } catch (err: any) {
      console.error(`error from ${requestName}: `, err)
      const notLogout = requestName !== 'LogoutMutation'

      if (notLogout && await this.handleUnauthorized(err)) {
        return this.getResponse<any>(err as FetchResponseError)
      }

      const res = this.getResponse<any>(err as FetchResponseError)

      if (notLogout) {
        if (res.validationErrors) {
          this.showValidationErrors(res.validationErrors)
        } else {
          this.showErrorNotification(res.message)
        }
      }

      return res
    }
  }

  public async requestQuery(
    requestType: QueryType,
    requestName: string,
    variables?: Record<string, any>,
  ) {
    try {
      const args = variables && Object.keys(variables).length > 0
        ? `(${Object.entries(variables)
          .map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
          .join(', ')})`
        : ''

      const query = `
      ${requestType} ${requestName.charAt(0).toUpperCase() + requestName.slice(1)} {
          ${requestName}${args}
      }
    `
      // console.warn(`debug query from ${requestName}: `, query)

      const response = await this.axios(query)

      if (response.errors) {
        throw response
      }
      return this.getResponse(response.data?.[requestName])
    } catch (err: any) {
      console.error(`error from ${requestName}: `, err)

      if (await this.handleUnauthorized(err)) {
        return this.getResponse<any>(err as FetchResponseError)
      }

      const res = this.getResponse<any>(err as FetchResponseError)

      if (res.validationErrors) {
        this.showValidationErrors(res.validationErrors)
      } else {
        this.showErrorNotification(res.message)
      }

      return res
    }
  }

  /**
   * Выполняет несколько query в одном HTTP-запросе, объединяя их в один GraphQL документ.
   * Возвращает объект с результатами по имени каждого запроса.
   */
  public async batchQuery(queries: BatchQueryItem[]): Promise<Record<string, any>> {
    try {
      const adaptedQueries = queries.map((q) => ({
        ...q,
        params: q.params ? adaptRequestParams(q.name, q.params) : undefined,
      }))

      const query = buildBatchQuery(adaptedQueries)
      const response = await this.axios(query)

      // Частичные field-level ошибки (например "access denied" на отдельное
      // поле вроде roles) не фатальны: GraphQL отдаёт data по полям, к которым
      // доступ есть. Гидрируем тем, что реально пришло, и не разлогиниваем юзера.
      if (response.errors) {
        console.warn('batchQuery partial errors:', response.errors)
        if (!response.data) {
          throw response
        }
      }

      return response.data ?? {}
    } catch (err: any) {
      console.error('batchQuery error:', err)

      if (await this.handleUnauthorized(err)) {
        return {}
      }

      throw err
    }
  }

}

const Srv = new GraphQLSrv()
export default Srv
